import { useFanforgeApi, apiStub, InferResponseType } from '@hotpot-arcade/fanforge-api-client';
import { useCallback, useEffect, useRef, useState } from 'react';
import useWebSocket from 'react-use-websocket';

type OverlayWsResponse = {
  type: string;
  payload: {
    userId: string;
    username: string;
    itemId: string;
    itemName: string;
    gameId: string;
  };
};
const DELAY_MS = 4000;

export function useAlertWebsocket(partnerId: string) {
  const apiClient = useFanforgeApi();
  const { lastMessage } = useWebSocket(
    apiClient.client.overlay[':creatorId'].ws.$url({ param: { creatorId: partnerId } }).href.replace(/^https:/, 'wss:'),
    { shouldReconnect: () => true, retryOnError: true, protocols: ['Authorization'] }
  );

  const [currentAlert, setCurrentAlert] = useState<OverlayWsResponse | null>(null);
  const queueRef = useRef<OverlayWsResponse[]>([]);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showNextAlert = useCallback(() => {
    if (queueRef.current.length === 0) {
      setCurrentAlert(null);
      return;
    }

    const next = queueRef.current.shift()!;
    setCurrentAlert(next);

    delayTimerRef.current = setTimeout(() => {
      setCurrentAlert(null);
      showNextAlert();
    }, DELAY_MS);
  }, []);

  useEffect(() => {
    if (!lastMessage) return;

    let data: OverlayWsResponse;
    try {
      data = JSON.parse(lastMessage.data);
    } catch {
      return;
    }

    queueRef.current.push({ ...data });

    setCurrentAlert((prev) => {
      if (!prev) {
        showNextAlert();
      }
      return prev;
    });
  }, [lastMessage, showNextAlert]);

  useEffect(() => {
    return () => {
      if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
    };
  }, []);

  return { currentAlert };
}
